import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Menu, Button, Tooltip, message } from "antd";
import { ReloadOutlined, LogoutOutlined } from "@ant-design/icons";
import ThemeToggle from "../ThemeToggle";
import { useTheme } from "../../contexts/ThemeContext";
import { useUser } from "../../contexts/UserContext";
import "./index.css";

const { SubMenu } = Menu;

const { Header, Content } = Layout;

const MenuLayout = ({ children }) => {
  const { pathname: path } = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { isAdmin, loading, retryCount, refreshUserData, logout, userProfile } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);



  const tabs = [
    {
      key: "/search",
      label: "搜索",
    },
    {
      key: "/my-profile",
      label: "个人资料",
    },
    {
      key: "/alum-map",
      label: "校友地图",
    },
    {
      key: "/about",
      label: "关于",
    },
    // Only show admin submenu for admin users
    ...(isAdmin ? [{
      key: "admin",
      label: "管理员",
      children: [
        {
          key: "/admin/add-user",
          label: "添加用户",
        },
        {
          key: "/admin/modify-users",
          label: "修改用户",
        },
        {
          key: "/admin/null-coordinates",
          label: "坐标异常用户",
        },
      ],
    }] : []),
  ];

  // Check if screen size is mobile
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleMenuClick = ({ key }) => {
    navigate(key);
    // Close mobile menu after navigation
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Close mobile menu if open
      if (isMobile && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
      
      const result = await logout();
      if (result.success) {
        message.success("退出登录成功");
        navigate("/login");
      } else {
        message.error("退出登录失败，请重试");
      }
    } catch (error) {
      message.error("退出登录失败，请重试");
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <Layout>
      <Header className="menu-header">
        <div className="menu-logo">
          <span className="logo-text">早培校友平台</span>
        </div>
        
        {/* Desktop Navigation */}
        <Menu
          theme={isDarkMode ? "dark" : "light"}
          mode="horizontal"
          selectedKeys={path}
          items={tabs}
          onClick={handleMenuClick}
          className={`menu-nav ${isMobile ? 'menu-nav-desktop-hidden' : ''}`}
        />
        
        {/* Mobile Hamburger Button */}
        <button
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        <div className="menu-actions">
          {(loading || retryCount > 0) && (
            <Tooltip title={retryCount > 0 ? "重新加载用户信息" : "正在加载..."}>
              <Button
                type="text"
                icon={<ReloadOutlined spin={loading} />}
                onClick={refreshUserData}
                disabled={loading}
                style={{ 
                  marginRight: '8px',
                  color: retryCount > 0 ? '#ff4d4f' : undefined
                }}
              />
            </Tooltip>
          )}
          {userProfile && (
            <Tooltip title="退出登录">
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                data-logout="true"
                style={{ 
                  marginRight: '8px',
                  color: '#666',
                  transition: 'all 0.3s ease'
                }}
              />
            </Tooltip>
          )}
          <ThemeToggle />
        </div>
      </Header>

      {/* Mobile Navigation Overlay */}
      {isMobile && (
        <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
          <nav className="mobile-nav">
            {tabs.map((tab) => {
              if (tab.children) {
                // Render submenu items for mobile
                return (
                  <div key={tab.key} className="mobile-submenu">
                    <div className="mobile-submenu-title">{tab.label}</div>
                    {tab.children.map((child) => (
                      <button
                        key={child.key}
                        className={`mobile-nav-item mobile-submenu-item ${path === child.key ? 'active' : ''}`}
                        onClick={() => handleMenuClick({ key: child.key })}
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <button
                  key={tab.key}
                  className={`mobile-nav-item ${path === tab.key ? 'active' : ''}`}
                  onClick={() => handleMenuClick({ key: tab.key })}
                >
                  {tab.label}
                </button>
              );
            })}
            {userProfile && (
              <button
                className="mobile-nav-item logout-item"
                onClick={handleLogout}
                style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid var(--border-primary)',
                  color: '#ff4d4f'
                }}
              >
                <LogoutOutlined style={{ marginRight: '8px' }} />
                退出登录
              </button>
            )}
          </nav>
        </div>
      )}

      <Layout>
        <Content className="menu-content">
          <div className="content-wrapper">
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MenuLayout;
